import { useEffect, useMemo, useRef, useState } from 'react'
import type { LucideIcon } from 'lucide-react'

export interface CommandItem {
  id: string
  label: string
  hint?: string
  icon: LucideIcon
  run: () => void
}

interface CommandPaletteProps {
  open: boolean
  onClose: () => void
  commands: CommandItem[]
}

export default function CommandPalette({ open, onClose, commands }: CommandPaletteProps) {
  const [query, setQuery] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!open) return
    setQuery('')
    const id = window.setTimeout(() => inputRef.current?.focus(), 30)
    return () => window.clearTimeout(id)
  }, [open])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return commands
    return commands.filter((c) => c.label.toLowerCase().includes(q))
  }, [commands, query])

  if (!open) return null

  const run = (command: CommandItem) => {
    onClose()
    command.run()
  }

  return (
    <div className="absolute inset-0 z-50 bg-black/30 flex justify-center pt-[12vh]" onClick={onClose}>
      <div
        role="dialog"
        aria-label="Comandos"
        className="w-[540px] max-w-[calc(100%-24px)] self-start bg-surface border border-line rounded-xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2.5 px-3.5 border-b border-line">
          <span className="text-faint text-sm">⌘</span>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && filtered[0]) run(filtered[0])
              if (e.key === 'Escape') onClose()
            }}
            placeholder="O que você quer fazer?"
            className="flex-1 h-12 bg-transparent text-sm text-ink outline-none"
          />
          <span className="font-mono text-[11px] text-faint">esc</span>
        </div>
        <div className="p-1.5 flex flex-col gap-0.5 max-h-[340px] overflow-auto">
          {filtered.length === 0 && (
            <p className="px-2.5 py-3 text-sm text-faint">Nenhum comando encontrado.</p>
          )}
          {filtered.map((command, index) => (
            <button
              key={command.id}
              type="button"
              onClick={() => run(command)}
              className={`flex items-center gap-2.5 h-9 px-2.5 rounded-md text-left text-[13px] text-ink cursor-pointer ${
                index === 0 ? 'bg-surface-2' : 'hover:bg-surface-2'
              }`}
            >
              <command.icon size={14} className="text-muted shrink-0" aria-hidden="true" />
              <span className="flex-1 truncate">{command.label}</span>
              {command.hint && <span className="text-[11.5px] text-faint">{command.hint}</span>}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
